import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import QRCodeStyling, { FileExtension, Options as QRStylingOptions } from 'qr-code-styling';
import { QrCode } from 'lucide-react';
import { QRCornerDotStyle, QRCornerSquareStyle, QRDotStyle } from '../../types/qr.types';
import { loadImageAsDataUrlOnce } from '../../utils/imageDataUrl';

// Internal render resolution — kept high regardless of on-screen display size (which is
// controlled by the container's CSS) so PNG/JPEG downloads are print-quality, not blurry.
const RENDER_SIZE = 1000;

export interface QRCodePreviewProps {
  value: string;
  backgroundColor?: string;
  foregroundColor?: string;
  logoUrl?: string;
  dotsType?: QRDotStyle;
  cornersSquareType?: QRCornerSquareStyle;
  cornersDotType?: QRCornerDotStyle;
  className?: string;
}

export interface QRCodePreviewHandle {
  download: (filename: string, extension?: FileExtension) => Promise<void>;
}

export const QRCodePreview = forwardRef<QRCodePreviewHandle, QRCodePreviewProps>(
  function QRCodePreview(
    {
      value,
      backgroundColor = '#FFFFFF',
      foregroundColor = '#000000',
      logoUrl,
      dotsType = 'square',
      cornersSquareType = 'square',
      cornersDotType = 'square',
      className,
    },
    ref
  ) {
    const containerRef = useRef<HTMLDivElement>(null);
    const qrRef = useRef<QRCodeStyling | null>(null);

    // Verify the logo actually loads before handing it to qr-code-styling — an invalid/404/CORS-blocked
    // URL would otherwise make the whole QR code fail to render instead of just omitting the logo.
    // Holds a data: URI (see loadImageAsDataUrlOnce above), not the original logoUrl.
    const [validatedLogoUrl, setValidatedLogoUrl] = useState<string | undefined>(undefined);

    useEffect(() => {
      if (!logoUrl) {
        setValidatedLogoUrl(undefined);
        return;
      }
      let cancelled = false;
      loadImageAsDataUrlOnce(logoUrl).then((dataUrl) => {
        if (!cancelled) setValidatedLogoUrl(dataUrl || undefined);
      });
      return () => {
        cancelled = true;
      };
    }, [logoUrl]);

    useEffect(() => {
      if (!containerRef.current || !value) return;

      const options: Partial<QRStylingOptions> = {
        type: 'svg',
        width: RENDER_SIZE,
        height: RENDER_SIZE,
        data: value,
        margin: Math.round(RENDER_SIZE * 0.04),
        image: validatedLogoUrl || undefined,
        qrOptions: { errorCorrectionLevel: validatedLogoUrl ? 'H' : 'M' },
        imageOptions: {
          crossOrigin: 'anonymous',
          margin: Math.round(RENDER_SIZE * 0.02),
          imageSize: 0.4,
          hideBackgroundDots: true,
        },
        dotsOptions: { type: dotsType, color: foregroundColor },
        cornersSquareOptions: { type: cornersSquareType, color: foregroundColor },
        cornersDotOptions: { type: cornersDotType, color: foregroundColor },
        backgroundOptions: { color: backgroundColor },
      };

      if (!qrRef.current) {
        qrRef.current = new QRCodeStyling(options);
        qrRef.current.append(containerRef.current);
      } else {
        qrRef.current.update(options);
      }

      // Let the rendered SVG scale to fill whatever size the caller's className gives the container.
      const svg = containerRef.current.querySelector('svg');
      if (svg) {
        svg.setAttribute('width', '100%');
        svg.setAttribute('height', '100%');
      }
    }, [value, backgroundColor, foregroundColor, validatedLogoUrl, dotsType, cornersSquareType, cornersDotType]);

    useImperativeHandle(ref, () => ({
      download: async (filename: string, extension: FileExtension = 'png') => {
        if (!qrRef.current) return;
        await qrRef.current.download({ name: filename, extension });
      },
    }));

    if (!value) {
      return (
        <div className={className}>
          <QrCode className="w-full h-full text-gray-300" />
        </div>
      );
    }

    return <div ref={containerRef} className={className} />;
  }
);
